import Node from '../ui/Node';
import mixin from '../privates/utils/mixin';
import ViewportMixin from './mixins/ViewportMixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import _ from 'lodash';

const SLIDE_TYPES = new Set(['wysiwyg.viewer.components.BoxSlideShowSlide',
    'wysiwyg.viewer.components.StripContainerSlideShowSlide',
    'wysiwyg.viewer.components.StateBoxState',
    'wysiwyg.viewer.components.StateBoxFormState',
    'wysiwyg.viewer.components.StateStripState']);

/**
 * @mixin Element
 * @summary Provides basic functionality for elements.
 * @description
 *  `Element` is a mixin that provides basic properties, functions, and event handlers for elements on the page.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *   $w("#Element").onViewportEnter( (event) => {
 *     let targetId = event.target.id; 
 *   });
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `Element`. 
 * 
 *   ```javascript
 *   $w("#myElement").onViewportEnter( (event) => {
 *     let targetId = event.target.id; 
 *   });
 *  ```
 *  
 * @mixes $w.Node
 * @mixes $w.ViewportMixin
 * @memberof $w
 */
class Element extends Node {


    // PROPERTIES:

    /**
     * @member rendered
     * @label property
     * @summary Indicates if an element is currently in the [DOM structure](https://developer.mozilla.org/en-US/docs/Web/API/Document_Object_Model/Introduction).
     * @description
     *  If `rendered` is `true`, the element is in the current DOM structure and
     *  can be used.
     *
     *  If `rendered` is `false` the element is not in the current DOM structure.
     *
     *  An element might not be in the DOM if it is in a [slide]($w.Slide.html) or a [state]($w.State.html)
     *  which is not currently showing.
     * @snippet [Element-rendered.es6=Get an element's rendered status]
     * @type {external:Boolean}
     * @memberof $w.Element
     * @see [collapsed]($w.HiddenCollapsedMixin.html#collapsed)
     * @see [hidden]($w.HiddenCollapsedMixin.html#hidden)
     * @see [isVisible]($w.HiddenCollapsedMixin.html#isVisible)
     * @readonly
     */
    get rendered() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        if (RMI.isGhostComponent(compId)) {
            return false;
        }

        let parentId = compId;
        while (parentId) {
            const parentType = _.get(RMI.getComp(parentId), 'type');
            if (SLIDE_TYPES.has(parentType)) {
                const slideId = parentId;
                const slideshowId = RMI.getParent(parentId);
                const currentSlideIndex = _.get(RMI.getState(slideshowId), 'currentIndex');
                const displayedSlideId = _.get(RMI.getChildren(slideshowId), currentSlideIndex);
                return slideId === displayedSlideId;
            }
            parentId = RMI.getParent(parentId);
        }
        return RMI.getComp(compId).isDisplayed;
    }

    // METHODS:

    /**
     * @function onMouseIn
     * @summary Adds an event handler that runs when the mouse pointer is moved
     *  onto the element.
     * 
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the mouse pointer is moved onto the
     *  element.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [Element-onMouseIn.es6=Get the mouse event info when the mouse enters an element]
     * @eventtype mouseenter
     * @memberof $w.Element
     * @eventhandler
     */
    onMouseIn(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onMouseIn',
            dataToValidate: {onMouseIn: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().MOUSE_IN, controllerId, cb);
        return this;
    }

    /**
     * @function onMouseOut
     * @summary Adds an event handler that runs when the mouse pointer is moved
     *  off of the element.
     * 
     *  > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/tips-tutorials-examples/removal-of-the-w-parameter-from-event-handlers).
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the mouse pointer is moved off of
     *  the element.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [Element-onMouseOut.es6=Get the mouse event info when the mouse exits an element]
     * @eventtype mouseleave
     * @memberof $w.Element
     * @eventhandler
     */
    onMouseOut(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onMouseOut',
            dataToValidate: {onMouseOut: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().MOUSE_OUT, controllerId, cb);
        return this;
    }

    toJSON() {
        const {id, type, global, rendered} = this;
        return {id, type, global, rendered};
    }
}

mixin(Element.prototype, ViewportMixin.prototype);

export default Element;
